/**
 * Shared Risk Analyst supplier legal-name resolution (fetch + rank).
 * Used by profile/commentary answers and the live recognition test script.
 * Callers must pass a Supabase client (avoid importing the browser app client here).
 */
import type { SupabaseClient } from '@supabase/supabase-js'
import { hideSimulatedSuppliers } from './simulatedSuppliers'
import {
  hasAnyRiskScores,
  longestSupplierSearchToken,
  normalizeSupplierName,
  rankSupplierNameMatches,
  supplierSearchTokens,
  type RiskRatingSupplier,
} from './supplierRiskRatingsTable'

export const SUPPLIER_PROFILE_SELECT =
  'id, name, location, kenvue_region, category, brand, overall_score, risk_gauge, risk_scores, risk_dimension_narratives, highest_risk_areas, supplier_unique_id, single_source, updated_at, procurement_type, parent_name, spend_k, dm_em'

const DEFAULT_SELECT = SUPPLIER_PROFILE_SELECT

export type SupplierRowWithId = RiskRatingSupplier & { id: string }

export type ResolveSupplierLegalNameResult =
  | { status: 'unique'; chosenName: string; chosen: SupplierRowWithId; rows: SupplierRowWithId[] }
  | { status: 'ambiguous'; candidates: string[]; rows: SupplierRowWithId[] }
  | { status: 'none'; rows: SupplierRowWithId[] }

export type NameResolveClient = Pick<SupabaseClient, 'from'>

export async function fetchSuppliersByName(
  nameQuery: string,
  client: NameResolveClient,
  options?: { select?: string; limit?: number }
): Promise<SupplierRowWithId[]> {
  const select = options?.select ?? DEFAULT_SELECT
  const limit = options?.limit ?? 120

  const q = normalizeSupplierName(nameQuery.replace(/&/g, ' '))
  if (!q) return []
  const tokens = supplierSearchTokens(nameQuery)

  const { data: phraseData, error: phraseErr } = await client
    .from('suppliers')
    .select(select)
    .ilike('name', `%${escapeIlike(q)}%`)
    .order('name')
    .limit(limit)

  if (phraseErr) {
    throw new Error(phraseErr.message || 'Failed to load suppliers by name.')
  }

  let rows = (phraseData ?? []) as unknown as SupplierRowWithId[]
  if (rows.length > 0) {
    return hideSimulatedSuppliers(filterByAllTokens(rows, tokens))
  }

  const primary = longestSupplierSearchToken(nameQuery)
  if (!primary) return []

  const { data, error } = await client
    .from('suppliers')
    .select(select)
    .ilike('name', `%${escapeIlike(primary)}%`)
    .order('name')
    .limit(limit)

  if (error) {
    throw new Error(error.message || 'Failed to load suppliers by name.')
  }

  rows = filterByAllTokens((data ?? []) as unknown as SupplierRowWithId[], tokens)

  if (rows.length === 0 && tokens.length > 0 && tokens[0] !== primary) {
    const { data: again, error: err2 } = await client
      .from('suppliers')
      .select(select)
      .ilike('name', `%${escapeIlike(tokens[0])}%`)
      .order('name')
      .limit(limit)
    if (err2) {
      throw new Error(err2.message || 'Failed to load suppliers by name.')
    }
    rows = filterByAllTokens((again ?? []) as unknown as SupplierRowWithId[], tokens)
  }

  return hideSimulatedSuppliers(rows)
}

/**
 * Resolve a name query to a unique legal supplier name using production ranking.
 */
export async function resolveSupplierLegalName(
  nameQuery: string,
  client: NameResolveClient,
  options?: { select?: string; preferScored?: boolean }
): Promise<ResolveSupplierLegalNameResult> {
  const rows = await fetchSuppliersByName(nameQuery, client, { select: options?.select })
  if (rows.length === 0) {
    return { status: 'none', rows }
  }

  const preferScored = options?.preferScored !== false
  const withScores = preferScored ? rows.filter(hasAnyRiskScores) : rows
  const pool = withScores.length > 0 ? withScores : rows

  const byName = new Map<string, SupplierRowWithId>()
  for (const row of pool) {
    const key = normalizeSupplierName(row.name || '').toLowerCase()
    if (!key) continue
    const existing = byName.get(key)
    if (!existing) {
      byName.set(key, row)
      continue
    }
    if (countScores(row) > countScores(existing)) {
      byName.set(key, row)
      continue
    }
    if (countScores(row) === countScores(existing)) {
      const a = Number(row.overall_score)
      const b = Number(existing.overall_score)
      if (Number.isFinite(a) && (!Number.isFinite(b) || a > b)) byName.set(key, row)
    }
  }

  const unique = [...byName.values()]
  if (unique.length === 0) {
    return { status: 'none', rows }
  }

  const ranked = rankSupplierNameMatches(nameQuery, unique)
  if (ranked.kind === 'unique') {
    return {
      status: 'unique',
      chosenName: normalizeSupplierName(ranked.row.name || ''),
      chosen: ranked.row,
      rows,
    }
  }
  if (ranked.kind === 'ambiguous') {
    return {
      status: 'ambiguous',
      candidates: ranked.rows.map((r) => normalizeSupplierName(r.name || '')).filter(Boolean),
      rows,
    }
  }
  return { status: 'none', rows }
}

/** Short alias for recognition tests: first 1–2 significant tokens. */
export function shortSupplierAlias(legalName: string): string | null {
  const tokens = supplierSearchTokens(legalName)
  if (tokens.length === 0) return null
  if (tokens.length === 1) return tokens[0]
  return `${tokens[0]} ${tokens[1]}`
}

/**
 * True when alias uniquely identifies expectedName among allNames
 * (exactly one name starts with alias, or exactly one contains all alias tokens).
 */
export function isAliasUniqueForName(
  alias: string,
  expectedName: string,
  allNames: string[]
): boolean {
  const q = normalizeSupplierName(alias).toLowerCase()
  const expected = normalizeSupplierName(expectedName).toLowerCase()
  if (!q || !expected) return false

  const starts = allNames.filter((n) => normalizeSupplierName(n).toLowerCase().startsWith(q))
  if (starts.length === 1 && normalizeSupplierName(starts[0]).toLowerCase() === expected) return true

  const tokens = supplierSearchTokens(alias)
  const tokenHits = allNames.filter((n) => {
    const hay = normalizeSupplierName(n).toLowerCase()
    return tokens.every((t) => hay.includes(t.toLowerCase()))
  })
  return (
    tokenHits.length === 1 && normalizeSupplierName(tokenHits[0]).toLowerCase() === expected
  )
}

function filterByAllTokens(rows: SupplierRowWithId[], tokens: string[]): SupplierRowWithId[] {
  if (tokens.length <= 1) return rows
  return rows.filter((r) => {
    const n = normalizeSupplierName(r.name || '').toLowerCase()
    return tokens.every((t) => n.includes(t.toLowerCase()))
  })
}

function countScores(s: RiskRatingSupplier): number {
  let n = 0
  const rs = s.risk_scores
  if (!rs || typeof rs !== 'object') return 0
  for (const v of Object.values(rs)) {
    if (v != null && Number.isFinite(typeof v === 'number' ? v : Number(v))) n++
  }
  return n
}

function escapeIlike(value: string): string {
  return value.replace(/\\/g, '\\\\').replace(/%/g, '\\%').replace(/_/g, '\\_')
}
